import { withSuspense } from '@atlassian/jira-platform-resources-utils/src/utils/with-suspense/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import type { ProjectContext as ProjectContextPlatformType } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import {
	type ProjectKey,
	type ProjectId,
	toProjectKey,
	toProjectId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { useResource } from '@atlassian/react-resource-router';

export const useProjectContextResource = withSuspense(() =>
	useResource<ProjectContextPlatformType | null>(projectContextResource),
);

export type ProjectContext = Omit<ProjectContextPlatformType, 'projectId'> & {
	projectKey: ProjectKey;
	projectId: ProjectId;
};

export const useProjectContext = (): ProjectContext => {
	const { data, error } = useProjectContextResource();

	if (data == null) {
		throw new Error(
			`data of useProjectContext via withSuspense shouldn't be null. If projectContext is null, this means an invalid project key was provided and the page should return a 404 and useProjectContext should never be called. error: ${
				error !== null ? error.message : 'null'
			}`,
		);
	}

	return {
		...data,
		projectKey: toProjectKey(data.projectKey),
		projectId: toProjectId(data.projectId.toString()),
	};
};

type ProjectAnalyticsAttributes = {
	projectId: ProjectId;
	isProjectAdmin: boolean;
	isSimplified: boolean;
};

export const useProjectAnalyticsAttributes = (): ProjectAnalyticsAttributes => {
	const { projectId, isProjectAdmin, simplified } = useProjectContext();

	return {
		isSimplified: simplified,
		isProjectAdmin,
		projectId,
	};
};
